import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase, setMediaSession, invalidateMedia } from "./supabase";

const AuthContext = createContext(null);

export async function eraseOpsLocalSession(auth, storage = window.localStorage,
  projectUrl = process.env.REACT_APP_SUPABASE_URL) {
  // supabase.js uses createClient's default browser storage/key. auth-js 2.89
  // returns before removing it on network failure, even with scope: "local".
  // Remove only this client's keys, never another project's or app's storage.
  const key = `sb-${new URL(projectUrl).hostname.split(".")[0]}-auth-token`;
  const keys = [key, `${key}-code-verifier`, `${key}-user`];
  for (const item of keys) storage.removeItem(item);
  // With the token removed, the PUBLIC SDK path clears its remaining state and
  // broadcasts SIGNED_OUT without needing a successful revocation request.
  const { error } = await auth.signOut({ scope: "local" });
  if (error) throw error;
  const { data, error: sessionError } = await auth.getSession();
  if (sessionError) throw sessionError;
  if (data?.session || keys.some((item) => storage.getItem(item) !== null)) {
    throw new Error("Local session removal could not be verified.");
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(null);
  const [logoutRecovery, setLogoutRecovery] = useState(null);
  const generationRef = useRef(0);
  const activeUserIdRef = useRef(null);
  const employeeLoadedRef = useRef(false);
  const signingOutRef = useRef(false);
  const signOutPromiseRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    let authEventSeen = false;
    const initialGeneration = ++generationRef.current;

    function acceptSession(nextSession) {
      if (disposed || signingOutRef.current) return;
      const userId = nextSession?.user?.id || null;
      if (!userId) {
        setMediaSession(null);
        generationRef.current += 1;
        activeUserIdRef.current = null;
        employeeLoadedRef.current = false;
        setSession(null);
        setEmployee(null);
        setOrganizations([]);
        setError(null);
        setLoading(false);
        return;
      }

      setMediaSession(nextSession);
      setLogoutRecovery(null);
      setSession(nextSession);
      if (activeUserIdRef.current === userId && employeeLoadedRef.current) return;

      const generation = ++generationRef.current;
      activeUserIdRef.current = userId;
      employeeLoadedRef.current = true;
      setEmployee(null);
      setOrganizations([]);
      setError(null);
      setLoading(true);
      const isCurrent = () => !disposed
        && generationRef.current === generation
        && activeUserIdRef.current === userId;

      // Leave the Supabase auth callback before starting another auth-backed
      // request. The generation also invalidates queued and in-flight loads.
      Promise.resolve().then(() => loadEmployeeData(nextSession.user, isCurrent));
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      authEventSeen = true;
      acceptSession(event === "SIGNED_OUT" ? null : nextSession);
    });

    supabase.auth.getSession()
      .then(({ data, error: sessionError }) => {
        if (disposed || authEventSeen || generationRef.current !== initialGeneration) return;
        if (sessionError) throw sessionError;
        acceptSession(data?.session || null);
      })
      .catch(() => {
        if (disposed || authEventSeen || generationRef.current !== initialGeneration) return;
        acceptSession(null);
      });

    return () => {
      disposed = true;
      invalidateMedia();
      generationRef.current += 1;
      activeUserIdRef.current = null;
      employeeLoadedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  async function loadEmployeeData(user, isCurrent) {
    if (!isCurrent()) return;
    try {
      // The server verifies email ownership and resolves ambiguity atomically.
      // Never email-match, claim authority rows, or trust a browser role here.
      const { data, error: employeeError } = await supabase.rpc("t4s_link_current_employee");
      if (!isCurrent()) return;
      if (employeeError) throw employeeError;
      if (!Array.isArray(data) || data.length !== 1
          || !data[0]?.id || data[0].auth_user_id !== user.id
          || data[0].is_active !== true) {
        throw new Error("A unique active employee profile could not be verified. Contact your administrator.");
      }
      const emp = data[0];
      if (!emp.org_id) throw new Error("Your employee profile has no primary organization.");

      const { data: primaryOrg, error: primaryError } = await supabase
        .from("t4s_organizations").select("*").eq("id", emp.org_id).single();
      if (!isCurrent()) return;
      if (primaryError) throw primaryError;
      if (!primaryOrg || primaryOrg.id !== emp.org_id) {
        throw new Error("Your primary organization could not be loaded.");
      }

      const { data: memberships, error: membershipsError } = await supabase
        .from("t4s_employee_orgs")
        .select("org_id,is_primary,organization:t4s_organizations(*)")
        .eq("employee_id", emp.id);
      if (!isCurrent()) return;
      if (membershipsError) throw membershipsError;

      // Admin RPCs are scoped to the protected admin's home and explicit
      // memberships. employee_type=admin never means every organization.
      const byId = new Map();
      for (const membership of memberships || []) {
        if (!membership.organization || membership.organization.id !== membership.org_id) {
          throw new Error("An assigned organization could not be loaded.");
        }
        byId.set(membership.org_id, { ...membership, is_primary: membership.org_id === emp.org_id });
      }
      byId.set(emp.org_id, { org_id: emp.org_id, is_primary: true, organization: primaryOrg });
      setEmployee({ ...emp, organization: primaryOrg });
      setOrganizations([...byId.values()]);
      setLoading(false);
    } catch (err) {
      if (!isCurrent()) return;
      employeeLoadedRef.current = false;
      setEmployee(null);
      setOrganizations([]);
      setError(err?.message || "Error loading user data");
      setLoading(false);
    }
  }

  function signOut() {
    if (signingOutRef.current) return signOutPromiseRef.current;
    signingOutRef.current = true;
    setMediaSession(null);
    invalidateMedia();
    setSigningOut(true);
    // Close the local boundary immediately, even if remote sign-out is slow or
    // fails. Old employee/org requests must never repopulate the logged-out UI.
    const generation = ++generationRef.current;
    activeUserIdRef.current = null;
    employeeLoadedRef.current = false;
    setSession(null);
    setEmployee(null);
    setOrganizations([]);
    setError(null);
    setLogoutRecovery(null);
    setLoading(false);
    // auth-js removes its local session only after remote revocation finishes.
    // Keep Login unmounted until that destructive SDK completion has settled.
    const operation = (async () => {
      try {
        // Quiesce background refresh before the SDK's serialized sign-out so a
        // late refresh cannot rewrite the token after fallback storage cleanup.
        await supabase.auth.stopAutoRefresh?.();
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) throw signOutError;
        await supabase.auth.startAutoRefresh?.();
      } catch (err) {
        let localCleared = false;
        try {
          await eraseOpsLocalSession(supabase.auth);
          localCleared = true;
        } catch (cleanupError) {
          console.error("Could not verify local sign-out:", cleanupError);
        }
        if (generationRef.current === generation) {
          setLogoutRecovery({ localCleared });
          setError(`Remote sign-out was not confirmed: ${err?.message || "network failure"}. No global revocation was confirmed. ${localCleared
            ? "This browser's saved session was removed. Other sessions may still be active."
            : "Local session removal could not be verified. Do not reload or leave this browser unattended; retry sign-out."}`);
        }
        throw err;
      } finally {
        signingOutRef.current = false;
        signOutPromiseRef.current = null;
        if (generationRef.current === generation) setSigningOut(false);
      }
    })();
    // Existing click handlers do not await this promise. Mark the rejection as
    // handled while still returning it to callers that explicitly await logout.
    operation.catch(() => {});
    signOutPromiseRef.current = operation;
    return operation;
  }

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user,
      employee,
      organizations,
      loading: loading || signingOut,
      signingOut,
      error,
      logoutRecovery,
      signOut,
      isAuthenticated: !!session && !!employee,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
